import type { LiveLabSpeaker, LiveLabTurn, ParsedTranscript } from '@/types/livelab';

const REP_ALIASES = new Set([
  'rep',
  'reps',
  'sales',
  'salesperson',
  'seller',
  'ae',
  'sdr',
  'bdr',
  'agent',
  'me',
  'you',
  'self',
  'host',
]);
const CUSTOMER_ALIASES = new Set([
  'customer',
  'client',
  'prospect',
  'buyer',
  'cust',
  'lead',
  'caller',
  'them',
  'guest',
]);

/** Strips a leading timestamp like `[00:01:23]`, `(0:42)`, or `12:34` and returns it as seconds plus the remainder. */
function extractTimestamp(line: string): { at_s?: number; rest: string } {
  const m = line.match(/^\s*[[(]?\s*(\d{1,2}):(\d{2})(?::(\d{2}))?\s*[\])]?\s+(.*)$/);
  if (!m) return { rest: line };
  const a = parseInt(m[1]!, 10);
  const b = parseInt(m[2]!, 10);
  const c = m[3] !== undefined ? parseInt(m[3], 10) : undefined;
  const at_s = c !== undefined ? a * 3600 + b * 60 + c : a * 60 + b;
  return { at_s, rest: m[4] ?? '' };
}

/** Matches a `Speaker:` prefix at the start of a line. Speaker = 1-3 words, mostly letters. */
function extractSpeaker(line: string): { label?: string; rest: string } {
  const m = line.match(/^([A-Za-z][A-Za-z .'-]{0,40}?):\s*(.*)$/);
  if (!m) return { rest: line };
  const label = m[1]!.trim();
  if (label.length === 0 || label.length > 40) return { rest: line };
  return { label, rest: m[2] ?? '' };
}

function classifyLabel(label: string): LiveLabSpeaker | null {
  const norm = label.toLowerCase().replace(/[^a-z]/g, '');
  if (REP_ALIASES.has(norm)) return 'rep';
  if (CUSTOMER_ALIASES.has(norm)) return 'customer';
  return null;
}

/**
 * Parse a raw transcript into turns.
 *
 * Recognized formats (mix-and-match within one transcript):
 *   `Rep: Hello there.`
 *   `Customer: Hi, what is this about?`
 *   `[00:01:23] Sarah: Tell me about your business.`
 *   `Sarah: continued thought on the next line`  (continuation lines without a prefix get appended to the previous turn)
 *
 * If the prefix is a generic alias (Rep, Customer, AE, Sales, Client, etc.)
 * the speaker is auto-classified. If it's a personal name (Sarah, Mike), the
 * parser leaves it unclassified and `auto_classified` is false — the caller
 * must ask the user which name is the rep.
 */
export function parseTranscript(raw: string): ParsedTranscript {
  const lines = raw.split(/\r?\n/);
  const turns: LiveLabTurn[] = [];
  const labelOrder: string[] = [];
  const labelClass = new Map<string, LiveLabSpeaker | null>();

  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (!line) continue;

    const { at_s, rest: afterTs } = extractTimestamp(line);
    const { label, rest } = extractSpeaker(afterTs);

    if (label) {
      if (!labelOrder.includes(label)) {
        labelOrder.push(label);
        labelClass.set(label, classifyLabel(label));
      }
      const turn: LiveLabTurn = {
        speaker: labelClass.get(label) ?? 'customer',
        text: rest.trim(),
        label,
      };
      if (at_s !== undefined) turn.at_s = at_s;
      turns.push(turn);
    } else if (turns.length > 0) {
      const prev = turns[turns.length - 1]!;
      const joined = (prev.text + ' ' + line).trim();
      turns[turns.length - 1] = { ...prev, text: joined };
    }
  }

  const auto_classified =
    labelOrder.length > 0 && labelOrder.every((l) => labelClass.get(l) !== null);

  return { turns, labels: labelOrder, auto_classified };
}

/** Re-apply a user-supplied label→speaker mapping to a previously parsed transcript. */
export function applySpeakerMap(
  parsed: ParsedTranscript,
  map: Record<string, LiveLabSpeaker>,
): LiveLabTurn[] {
  return parsed.turns.map((t): LiveLabTurn => {
    const mapped = t.label !== undefined ? map[t.label] : undefined;
    return { ...t, speaker: mapped ?? t.speaker };
  });
}
